/**
 * The collector portal — its rail, headers and record rules — and the copy
 * for the artist's resale-settlements screen that hangs off it.
 *
 * A collector is the personal workspace the shell mounts by default; the
 * spec here is what `definePortal` checks total against `CollectorDestination`
 * in `collector.model.ts`, so a new destination without a heading or record
 * rule does not compile. Each screen on the rail owns its own copy elsewhere,
 * except the settlements screen, whose words sit here beside
 * `artistSettlements.model.ts`.
 *
 * The settlements copy is deliberately narrower than the gallery's: an artist
 * sees every figure that produced their share, including the rate, and never
 * the resale's buyer — the disclosure argument reaches the covenant holder
 * and stops there.
 */

import { FOCUS_PARAMS } from "../routes/routes.constants.ts";
import { definePortal } from "../shell/portal.ts";
import type { CollectorDestination } from "./collector.model.ts";

export const COLLECTOR_COPY = {
  registerAction: "Register Artwork",
  newCollectionAction: "New collection",
  eyebrow: "Collector",
  navLabel: "Collector workspace",
  /**
   * Shown in the issue form's place when there is nothing to issue for.
   *
   * Says REGISTER rather than "you lack a permission", because that is the whole
   * of what is missing: `issue_share_code` accepts any work the caller owns, and
   * somebody holding none is one registration away from the form appearing.
   */
  shareCodesNeedWork:
    "There is nothing here to issue a code for yet. Register a work of your own, and you can show it to one person for a while without giving them anything else.",
} as const;

/**
 * The artist's side of a resale settlement.
 *
 * NARROWER THAN THE GALLERY'S, deliberately. The artist is shown every figure
 * that produced their share — including the rate, because disputing a
 * remittance means disputing the rate that sized it — and NOT the resale's
 * buyer. Decision 3 of 20260906090000 discloses the buyer to the covenant
 * holder because a disputed settlement is resolved by knowing the parties; that
 * argument does not reach a party who is merely owed money out of it.
 */
export const ARTIST_SETTLEMENT_COPY = {
  loading: "Reading what you are owed\u2026",
  errorTitle: "Can\u2019t load your resale settlements",

  empty: "No covenant has paid out on your work yet.",
  emptyDetail:
    "When a gallery sells your work under a post-sale covenant that elects you, and the work is sold again on ONDO inside its term, your share of the profit appears here. Most resales happen privately and off the registry, and those leave nothing for a covenant to attach to.",

  ledger: (n: number) => `${n} ${n === 1 ? "settlement" : "settlements"}`,

  /**
   * The strip of totals above the list \u2014 OND-70.
   *
   * ONE PAIR OF TILES PER CURRENCY, NEVER A SUM ACROSS THEM. Every settlement is
   * denominated in its own covenant's currency, and \u00a3172,000 plus $40,000 is not
   * a number. The gallery's screen draws the same line as a sentence and gives
   * up when the currencies mix; the artist's draws a pair per currency instead,
   * because an estate with covenants in two currencies is the ordinary case.
   *
   * "Recorded as paid", not "paid": ONDO moves no money and observes no payment;
   * the figure is what galleries have SAID they passed on.
   */
  totalsLabel: "Totals by currency",
  totals: {
    owed: (currency: string) => `Owed to you \u00b7 ${currency}`,
    paid: (currency: string) => `Recorded as paid \u00b7 ${currency}`,
    rows: (n: number) => `${n} ${n === 1 ? "settlement" : "settlements"}`,
    awaiting: (n: number) => `${n} awaiting a rate`,
    nothing: (n: number) => `${n} owing nothing`,
  },

  lead: "A covenant\u2019s terms were fixed when the gallery first sold the work.",
  leadDetail:
    "Your share falls due automatically when the work is resold inside the term \u2014 nobody can vary it, and nobody can refuse it. The gallery collects it and passes your half on. ONDO records what is owed and who says they have paid it; it does not move money, which is why you are asked to confirm.",

  groups: {
    owed: "Owed to you",
    answer: "Waiting on your answer",
    closed: "Answered",
  },

  state: {
    due: "Owed",
    awaiting_rate: "Awaiting a rate",
    collected: "Collected by the gallery",
    settled: "Paid",
    nothing_owed: "Nothing owed",
    void: "Resale withdrawn",
  },

  figures: {
    original: "Original sale",
    resale: "Resale price",
    converted: (from: string, to: string) => `Resale in ${to} (from ${from})`,
    profit: "Profit",
    share: (percent: string) => `Covenant share (${percent})`,
    yours: (percent: string) => `Your share (${percent})`,
  },

  owedLabel: "Owed to you",
  pending: "\u2014",
  owedBy: "Owed by",
  resoldOn: "resold",
  fixedOn: "sold to the first buyer",
  rateStated: "rate stated by the gallery",

  notes: {
    due: "The gallery has not yet recorded collecting this from the reseller.",
    awaitingRate: (from: string, to: string) =>
      `The resale was recorded in ${from} and the covenant is in ${to}. The gallery has to state a rate before your share can be worked out \u2014 and when they do, you can dispute it here.`,
    collected: "The gallery has collected this and has not yet recorded passing your share on.",
    remitted:
      "The gallery has recorded paying you. Confirm it if the money arrived, or dispute it if it did not \u2014 your answer is recorded beside theirs and never overwrites it.",
    confirmed: "You confirmed you received this.",
    disputed: "You disputed this.",
    void: "The resale behind this settlement was withdrawn, so nothing is owed under it.",
  },

  actions: {
    confirm: "Confirm received",
    dispute: "Dispute",
    submitDispute: "Record the dispute",
    cancel: "Cancel",
    notePlaceholder: "What is wrong with it? Optional, and the gallery will see it.",
  },
} as const;

/**
 * The collector portal.
 *
 * `destinations` is the sidebar, in order. `state` is not a feature flag on a
 * finished feature — it says whether the registry can answer the question the
 * destination asks. A `planned` entry is still reachable, because a greyed-out
 * nav item tells the user nothing while a page naming what is coming tells them
 * something. `group` splits the list in two — see `NavGroup`.
 *
 * `headers`, `records` and `notYet` are checked TOTAL against
 * `CollectorDestination` by `definePortal`, so adding a destination without
 * deciding its heading or its record rule does not compile. That is stronger
 * than what this file had before, where `recordRule` was an `if` chain in the
 * model and a new destination silently got "none".
 */
export const COLLECTOR_PORTAL = definePortal<CollectorDestination>({
  eyebrow: COLLECTOR_COPY.eyebrow,
  navLabel: COLLECTOR_COPY.navLabel,

  destinations: [
    { id: "dashboard", label: "Dashboard", glyph: "⊞", state: "ready", group: "main" },
    { id: "collection", label: "Collections", glyph: "◫", state: "ready", group: "main" },
    // OND-52. AFTER Collections, because it is the same holdings read across
    // every legal owner rather than a different kind of thing -- and the
    // walkthrough's own order: "see all of your collection all in one place,
    // even though the ownership might be in separate entities".
    { id: "entities", label: "All Entities", glyph: "⧉", state: "ready", group: "main" },
    { id: "register", label: "Register Artwork", glyph: "✦", state: "ready", group: "main" },
    { id: "loans", label: "Loans, Consign & Agents", glyph: "⇄", state: "ready", group: "main" },
    { id: "viewing-rooms", label: "Viewing Rooms", glyph: "▣", state: "ready", group: "main" },
    { id: "verifications", label: "Verifications", glyph: "✓", state: "ready", group: "main" },
    // WHAT A COVENANT OWES YOU, from the other side of the gallery's screen.
    // Listed for a collector as well as an estate, because either can be the
    // artist a covenant elected — `works.attributed_principal_id` names the
    // artist and nothing says that principal is an estate. It reads empty for
    // everybody else, which is the honest answer rather than a hidden tab.
    // TITLE SOMEBODY HAS OFFERED YOU, and it is listed rather than hidden behind
    // a notification. A sale is recorded against an ONDO id by somebody else, so
    // the only thing that would otherwise tell a collector about it is a feed —
    // and CLAUDE.md is explicit that a screen reading a feed to answer "what is
    // true" is a bug. It reads empty for almost everybody, which is the honest
    // answer rather than a tab that appears and disappears.
    { id: "offers", label: "Offered to you", glyph: "→", state: "ready", group: "main" },
    // THE BORROWER'S HALF OF A LOAN, which had no screen anywhere until OND-9.
    // Named "Loans to you" rather than "Borrowed", so it reads as a thing that
    // has arrived rather than as a category of possession.
    { id: "borrowed", label: "Loans to you", glyph: "⇤", state: "ready", group: "main" },
    { id: "settlements", label: "Resale settlements", glyph: "⇄", state: "ready", group: "main" },
    // WHO HAS BEEN SHOWN A WORK OF YOURS, which the registry has recorded since
    // 20260910123000 and only the agent could read. `ondo.my_share_codes()` has
    // answered for an owner from the day it shipped — decision 6 of that
    // migration argues for it — so this entry is the whole of what the owner's
    // side of the feature was missing. The LABEL LIVES IN `headers` below, not
    // here: `toNav` renders the header title.
    { id: "share-codes", label: "Share Codes", glyph: "↗", state: "ready", group: "main" },
    { id: "notifications", label: "Notifications", glyph: "⊘", state: "ready", group: "foot" },
    { id: "notification-preferences", label: "Notification Settings", glyph: "⚙", state: "ready", group: "foot" },
    { id: "about", label: "About ONDO", glyph: "i", state: "ready", group: "foot" },
    { id: "activity", label: "Activity & access log", glyph: "≡", state: "ready", group: "foot" },
    // Also reachable from the identity card at the foot of the rail, which is the
    // only way in in both prototype portals. Listed as well, because a control
    // whose only affordance is that it happens to be clickable is one most people
    // never press — and this is where somebody goes to correct their own record.
    { id: "profile", label: "My Profile", glyph: "◉", state: "ready", group: "foot" },
    { id: "work", label: "Artwork record", glyph: "◈", state: "unlisted", group: "main" },
  ],

  /** Header title and subtitle for each destination. */
  headers: {
    dashboard: { title: "Dashboard", subtitle: "" },
    collection: {
      title: "Collections",
      // Plural, and the screen behind it is one grid over all of them with a
      // picker rather than a page per collection. Which collection holds a work
      // is the ownership fact; it is not a place to navigate to.
      subtitle: "Your artworks, where they are, and what the registry says about them",
    },
    entities: {
      title: "All entities",
      // Says the thing a reader would otherwise have to work out: the entities
      // are LEGAL OWNERS, and a work belongs to exactly one of them -- OND-40.
      subtitle: "Everything you own across every entity you act for, with each work's owner named",
    },
    register: {
      title: "Register Artwork",
      subtitle: "Add a new work to your collection on ONDO",
    },
    loans: {
      title: "Loans, Consign & Agents",
      // Names what the screen holds rather than repeating the rail, and does
      // not promise loans — the screen's own "Not recorded here" says why.
      subtitle: "What is out — who represents your works, and which a gallery holds",
    },
    "viewing-rooms": {
      title: "Viewing Rooms",
      subtitle: "Pages you can send to someone, showing the works you choose and nothing else",
    },
    verifications: {
      title: "Verifications",
      subtitle: "Confirm or dispute ONDO information others have asked you to verify",
    },
    offers: {
      title: "Offered to you",
      // Says what the screen is FOR in the second clause, because the first is
      // ambiguous on its own: a collector who has been offered nothing needs to
      // know this is where an offer would arrive, not that something is missing.
      subtitle: "Works a gallery has sold to your ONDO id, and which are yours once you accept",
    },
    borrowed: {
      title: "Loans to you",
      // Says what the screen is FOR in the second clause, the way `offers`
      // does: somebody who has been offered no loan needs to know this is
      // where one would arrive, not that something is missing. And it names
      // the distinction the whole feature rests on — possession moves, title
      // does not.
      subtitle:
        "Works somebody has offered to lend you. Accepting moves where a work is and nothing about who owns it",
    },
    settlements: {
      title: "Resale settlements",
      // Says who owes it, because that is the fact an artist most needs and the
      // one they cannot infer: a covenant is between the RESELLER and the
      // gallery that wrote it, and the artist's share is passed on by the
      // gallery rather than paid to them by the buyer.
      subtitle: "What a gallery's post-sale covenant owes you when your work is resold",
    },
    "share-codes": {
      // "Share Codes" AND NOTHING ELSE, and this string is the rail's label as
      // well as the heading — `toNav` calls `titleFor`, so `destinations[].label`
      // above is not what the sidebar renders. The agent's reads "Share Codes /
      // Agree a Consignment" because an agent's second half is placing a client's
      // work to a gallery. An owner's own sale is not a rail destination at
      // all, so naming one here would point at a screen that is not there.
      //
      // THIS SAID "no direct-sale door at all (`Capabilities.transferWork` is
      // false for them and says why)" until OND-173, and then "the screen is
      // the half still owed". Both are now spent: the act and the screen both
      // exist, and the screen is a panel on a WORK's own record
      // (`sell/OwnerSalePanel.tsx`), because a sale is about one work and this
      // rail is about sets of them. The rail is unchanged for that reason and
      // not for a missing feature.
      title: "Share Codes",
      // Says who came through, because that is the half no other screen carries.
      // Nothing in the app issued an owner's code before this — the work record
      // has no such control — so both halves are new here, and the ledger is the
      // one the registry was already keeping unread.
      subtitle: "Show one work to one person for a while — and see who came through",
    },
    notifications: {
      title: "Notifications",
      subtitle: "What the registry needs from you, and what has come back",
    },
    "notification-preferences": {
      title: "Notification Settings",
      subtitle:
        "How each kind of notification reaches you — immediately, in the daily digest, or not at all",
    },
    about: { title: "About ONDO", subtitle: "" },
    activity: {
      title: "Activity & access log",
      // Not "your audit trail": the log is not scoped to the reader's own acts, and
      // it carries activity rather than the grant ledger. See NOT_HERE.
      // OND-68. The second clause is new and is now true: reads are recorded,
      // on the access trail. A collector holds no seat at a firm, so there is
      // nobody else's reads to mention and no hedge to make.
      subtitle:
        "Every recorded act on the works and collections you can read, and every record you have opened",
    },
    profile: {
      title: "My Profile",
      // Replaced at render for an organisation's workspace, where the subject is
      // the entity rather than the reader — see PROFILE_HEADERS and `profileAt`.
      subtitle: "Account, identity verification and membership",
    },
    work: {
      title: "Artwork record",
      subtitle: "What the registry holds for this work, and its chain of title",
    },
  },

  /**
   * What a planned destination will do, and what has to exist first.
   *
   * EMPTY, and it was not always. "Loans, Consign & Agents" sat here from the
   * day the portal shipped with a page naming what was missing — first a
   * consignment grant and a transfer, then, once both landed, "the view ACROSS
   * works". `ondo.my_arrangements()` is that view and the screen reads it, so
   * the entry is gone rather than amended: a page naming what is missing is
   * only honest while something is. The map stays, because the type is total
   * over `planned` destinations and the next one will need it.
   */
  notYet: {},

  defaultDestination: "dashboard",

  /**
   * Whether a destination takes a fourth URL segment, and whether it needs one.
   *
   * Two do, and they differ. A WORK REQUIRES its record — a work record with no
   * work is not a screen, and the caller is sent to `recordFallback`, which is
   * where a record is chosen from. A VIEWING ROOM allows one: the list is a
   * screen in its own right, and a room id opens that room on top of it.
   */
  records: {
    dashboard: "none",
    collection: "none",
    // OND-52. One screen over every entity, so there is nothing a fourth
    // segment could name: an entity is not a place to navigate to, which is
    // the same sentence `collection`'s header above makes about a collection.
    entities: "none",
    register: "none",
    loans: "none",
    "viewing-rooms": "optional",
    verifications: "none",
    offers: "none",
    borrowed: "none",
    settlements: "none",
    "share-codes": "none",
    notifications: "none",
    "notification-preferences": "none",
    about: "none",
    activity: "none",
    profile: "none",
    work: "required",
  },

  recordFallback: "collection",
  badges: {
    verifications: "verifications",
    notifications: "notifications",
    // Remittances waiting on the artist's answer. The one row on that screen
    // with a control, and the one a gallery records unopposed if it is missed.
    settlements: "settlements",
  },
  profileAt: "profile",
  greetsAt: "dashboard",

  /**
   * The header action.
   *
   * Gated on a server-supplied capability, never on the role. A workspace is a
   * route, not a permission: opening a collector workspace grants nothing, so it
   * must not be what switches this button on. `registerWork` is false for someone
   * who holds no collection they may curate, and for them the button would open a
   * wizard with nowhere to submit to.
   */
  primaryAction: (capabilities, at) => {
    if (at === "register") return null;
    if (!capabilities.registerWork) return null;
    return { label: COLLECTOR_COPY.registerAction, destination: "register" };
  },

  /**
   * "New collection", on the Collections screen only, beside Register.
   *
   * It used to sit in a second row of buttons under the header, with its own
   * copy of Register Artwork beside it; the header is the one place a screen's
   * actions are drawn now. The link carries `FOCUS_PARAMS.create`, which the
   * Collections screen reads to open its form — the header is a frame and
   * holds none of the screen's state. Gated on the server's `createCollection`
   * for the reason `primaryAction` gives.
   */
  secondaryAction: (capabilities, at) => {
    if (at !== "collection") return null;
    if (!capabilities.createCollection) return null;
    return {
      label: COLLECTOR_COPY.newCollectionAction,
      destination: "collection",
      search: `${FOCUS_PARAMS.create}=1`,
    };
  },
});
